import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";
import GoogleButton from "@/components/GoogleButton";

const SIDE_IMG = "https://images.pexels.com/photos/4990531/pexels-photo-4990531.jpeg";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/account", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center px-4 py-16 sm:px-6 lg:px-16">
        <div className="w-full max-w-md">
          <Kicker>New Registration</Kicker>
          <h1 className="mt-4 font-display font-bold tracking-tight text-slate-900">
            <MaskedLine delay={0.1} className="text-4xl sm:text-5xl">
              Create your
            </MaskedLine>
            <MaskedLine delay={0.22} className="text-4xl text-blue-900 sm:text-5xl">
              account.
            </MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Free registration. Book NUC Service programs, pay fees online and track every
              booking from one place.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-8">
              <GoogleButton label="Sign up with Google" testId="google-signup-button" />
            </div>
            <div className="my-6 flex items-center gap-4" aria-hidden="true">
              <span className="h-px flex-1 bg-slate-200" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                or with email
              </span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              <div>
                <label htmlFor="register-name" className="field-label">Full name</label>
                <input
                  id="register-name"
                  data-testid="register-name-input"
                  className="field-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                  autoComplete="name"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label htmlFor="register-email" className="field-label">Email</label>
                <input
                  id="register-email"
                  type="email"
                  data-testid="register-email-input"
                  className="field-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="register-password" className="field-label">Password</label>
                  <input
                    id="register-password"
                    type="password"
                    data-testid="register-password-input"
                    className="field-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    placeholder="Min. 6 characters"
                  />
                </div>
                <div>
                  <label htmlFor="register-password-confirm" className="field-label">Confirm</label>
                  <input
                    id="register-password-confirm"
                    type="password"
                    data-testid="register-password-confirm-input"
                    className="field-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    placeholder="Repeat it"
                  />
                </div>
              </div>
              {error && (
                <p
                  data-testid="register-error-message"
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="register-submit-button"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Creating account…" : "Create account"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <p className="text-sm text-slate-600">
                Already registered?{" "}
                <Link
                  to="/login"
                  data-testid="register-login-link"
                  className="font-medium text-blue-900 hover:underline"
                >
                  Sign in instead
                </Link>
              </p>
            </form>
          </Reveal>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img
          src={SIDE_IMG}
          alt="NUC Service advisor guiding a small business owner"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" aria-hidden="true" />
        <div className="absolute bottom-10 left-10 right-10">
          <p className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Your services, tracked end to end.
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/70">
            NUC Service — National Union Corporation
          </p>
        </div>
      </div>
    </div>
  );
}
